import React, { useEffect, useState } from 'react';
import emailService, { AICampaignDesignRequest, AICampaignDesignResult, EmailCampaign, EmailPlaybookMeta } from '../../../services/emailService';
import { toast } from 'react-hot-toast';

interface GuidedLaunchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLaunch: (draft: Partial<EmailCampaign>) => void;
}

type WizardStep = 'goal' | 'playbook' | 'details' | 'review';

const goals: AICampaignDesignRequest['goal'][] = [
  'pipeline',
  'demo',
  'trial',
  'nurture',
  'reactivation',
  'event',
  'referral',
  'partnership',
  'recruiting',
];

const goalLabels: Record<AICampaignDesignRequest['goal'], string> = {
  pipeline: 'Fill pipeline (general outbound)',
  demo: 'Book product demos',
  trial: 'Drive free trials',
  nurture: 'Nurture warm leads',
  reactivation: 'Wake up cold leads',
  event: 'Promote an event or webinar',
  referral: 'Generate referrals',
  partnership: 'Open partnerships / channel',
  recruiting: 'Reach candidates',
};

const GuidedLaunchModal: React.FC<GuidedLaunchModalProps> = ({ isOpen, onClose, onLaunch }) => {
  const [step, setStep] = useState<WizardStep>('goal');
  const [goal, setGoal] = useState<AICampaignDesignRequest['goal']>('pipeline');
  const [industry, setIndustry] = useState('saas');
  const [sequenceLength, setSequenceLength] = useState<number | undefined>(5);
  const [playbooks, setPlaybooks] = useState<EmailPlaybookMeta[]>([]);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState<string | null>(null);
  const [targetAudience, setTargetAudience] = useState('Founders and revenue leaders at B2B startups');
  const [productDescription, setProductDescription] = useState('Write a short description of what you sell and why it is useful. Keep it plain English.');
  const [tone, setTone] = useState<AICampaignDesignRequest['tone']>('professional');
  const [length, setLength] = useState<AICampaignDesignRequest['length']>('medium');
  const [includeAIContent, setIncludeAIContent] = useState(true);
  const [isDesigning, setIsDesigning] = useState(false);
  const [designResult, setDesignResult] = useState<AICampaignDesignResult | null>(null);
  const [isLoadingPlaybooks, setIsLoadingPlaybooks] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep('goal');
    setDesignResult(null);
    setSelectedPlaybookId(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || step !== 'playbook') return;
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoadingPlaybooks(true);
        const meta = await emailService.getPlaybooks();
        if (cancelled) return;
        const filtered = meta.filter(pb => {
          const goalMatch = pb.goal === goal;
          const industryMatch = !industry || pb.industries.includes(industry) || pb.industries.includes('general');
          return goalMatch && industryMatch;
        });
        setPlaybooks(filtered.length ? filtered : meta);
      } catch (error) {
        console.error('Error loading playbooks for guided launch:', error);
        toast.error('Failed to load playbooks');
      } finally {
        if (!cancelled) {
          setIsLoadingPlaybooks(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, step, goal, industry]);

  if (!isOpen) return null;

  const handleNextFromGoal = () => {
    if (!goal) {
      toast.error('Choose a goal for this campaign');
      return;
    }
    setStep('playbook');
  };

  const handleNextFromPlaybook = () => {
    if (!selectedPlaybookId) {
      toast.error('Pick a playbook to continue');
      return;
    }
    setStep('details');
  };

  const handleDesign = async () => {
    if (!targetAudience || targetAudience.trim().length < 3) {
      toast.error('Describe who you are emailing in a simple sentence.');
      return;
    }
    if (!productDescription || productDescription.trim().length < 10) {
      toast.error('Add a short description of what you offer so we can write useful emails.');
      return;
    }
    try {
      setIsDesigning(true);
      const payload: AICampaignDesignRequest = {
        goal,
        industry,
        targetAudience,
        productDescription,
        tone,
        length,
        sequenceLength,
        includeAIContent,
      };
      const result = await emailService.designAICampaign(payload);
      setDesignResult(result);
      setStep('review');
    } catch (error) {
      console.error('Error designing AI campaign:', error);
      toast.error('Failed to design campaign. Please try again.');
    } finally {
      setIsDesigning(false);
    }
  };

  const handleLaunch = () => {
    if (!designResult) return;
    onLaunch(designResult.campaignDraft);
  };

  const renderGoalStep = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded-md px-3 py-2 text-xs text-blue-900">
        <div className="font-semibold mb-1">Start here if you are new to cold email</div>
        <p className="mb-1">We will help you choose a goal, pick a proven playbook, and generate a full sequence with AI-written drafts.</p>
        <p>Stick to simple descriptions  we will handle best practices like spacing, number of steps, and follow-up timing.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">What is your main goal?</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {goals.map(g => (
            <button
              key={g}
              type="button"
              onClick={() => setGoal(g)}
              className={`p-3 rounded border text-left transition-colors ${
                goal === g ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
              }`}
            >
              <div className="font-semibold text-gray-900 capitalize">{g}</div>
              <p className="text-xs text-gray-600 mt-1">{goalLabels[g]}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Industry (optional)</label>
          <input
            type="text"
            value={industry}
            onChange={e => setIndustry(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="e.g. saas, agency, recruiting"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Steps (optional)</label>
          <input
            type="number"
            min={3}
            max={10}
            value={sequenceLength || ''}
            onChange={e => setSequenceLength(e.target.value ? parseInt(e.target.value, 10) : undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
          <p className="mt-1 text-[11px] text-gray-500">Most beginners do well with 46 emails, 34 days apart.</p>
        </div>
        <div className="text-xs text-gray-500 flex items-end">
          <p>We will suggest a playbook that matches your goal and industry next.</p>
        </div>
      </div>
    </div>
  );

  const renderPlaybookStep = () => (
    <div className="space-y-4">
      <div className="text-sm text-gray-700">
        <p className="font-semibold mb-1">Choose a playbook pattern</p>
        <p className="text-xs text-gray-500">
          We have pre-built sequences that already follow best-practice timing and structure.
          Pick one that feels closest to what you want; you can always edit later.
        </p>
      </div>

      {isLoadingPlaybooks && (
        <div className="text-sm text-gray-500">Loading playbooks a0for your goal a0 b7 a0please wait…</div>
      )}

      {!isLoadingPlaybooks && (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {playbooks.map(pb => (
            <button
              key={pb.id}
              type="button"
              onClick={() => setSelectedPlaybookId(pb.id)}
              className={`w-full text-left border rounded-md p-3 text-sm transition-colors ${
                selectedPlaybookId === pb.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="font-semibold text-gray-900">{pb.name}</div>
                  <div className="mt-0.5 text-[11px] text-gray-500">{pb.description}</div>
                </div>
                <div className="flex flex-col items-end gap-1 text-xs text-gray-500">
                  <span className="px-2 py-0.5 rounded-full border border-gray-200">{pb.goal}</span>
                  <span className="px-2 py-0.5 rounded-full border border-gray-200">{pb.stepsCount} steps</span>
                </div>
              </div>
              <p className="text-[11px] text-gray-500">
                {pb.industries.join(', ')}  b7 {pb.useCases.join(', ')}
              </p>
            </button>
          ))}
          {playbooks.length === 0 && (
            <p className="text-xs text-gray-500">No playbooks found for this goal yet. Try removing the industry filter or go back and change the goal.</p>
          )}
        </div>
      )}
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-4">
      <div className="text-sm text-gray-700">
        <p className="font-semibold mb-1">Tell us who you are emailing and what you offer</p>
        <p className="text-xs text-gray-500">Use plain language. Imagine you are explaining this to a smart friend who is not in your industry.</p>
      </div>

      <div className="space-y-3 text-sm">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target audience</label>
          <input
            type="text"
            value={targetAudience}
            onChange={e => setTargetAudience(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="e.g. HR managers at mid-sized tech companies"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Your product or offer</label>
          <textarea
            rows={4}
            value={productDescription}
            onChange={e => setProductDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
          <p className="mt-1 text-[11px] text-gray-500">13 short sentences is enough. Focus on outcomes, not jargon.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
            <select
              value={tone}
              onChange={e => setTone(e.target.value as AICampaignDesignRequest['tone'])}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="professional">Professional</option>
              <option value="casual">Casual</option>
              <option value="friendly">Friendly</option>
              <option value="direct">Direct</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email length</label>
            <select
              value={length}
              onChange={e => setLength(e.target.value as AICampaignDesignRequest['length'])}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="short">Short (23 paragraphs)</option>
              <option value="medium">Medium</option>
              <option value="long">Longer, more detailed</option>
            </select>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input
              id="guided_include_ai_content"
              type="checkbox"
              checked={includeAIContent}
              onChange={e => setIncludeAIContent(e.target.checked)}
              className="h-4 w-4 text-purple-600 border-gray-300 rounded"
            />
            <label htmlFor="guided_include_ai_content" className="text-xs text-gray-700">
              Generate AI-written email content for each step
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-4">
      <div className="text-sm text-gray-700">
        <p className="font-semibold mb-1">Sequence ready to edit</p>
        <p className="text-xs text-gray-500">
          We have created a full multi-step sequence from a playbook and filled each step with AI-written drafts.
          On the next screen you can tweak subjects, content, and timing before you save.
        </p>
      </div>

      {designResult && (
        <div className="border border-gray-200 rounded-md p-3 text-xs text-gray-700 bg-gray-50">
          <p className="font-semibold mb-1">Playbook: {designResult.playbookName}</p>
          <p className="mb-1">Steps: {(designResult.campaignDraft.follow_up_steps || []).length}</p>
          {designResult.rationale && <p className="text-gray-600">{designResult.rationale}</p>}
        </div>
      )}

      {!designResult && (
        <p className="text-xs text-gray-500">Click "Generate sequence" to create your campaign draft.</p>
      )}
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'goal':
        return renderGoalStep();
      case 'playbook':
        return renderPlaybookStep();
      case 'details':
        return renderDetailsStep();
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  const stepIndex: Record<WizardStep, number> = {
    goal: 0,
    playbook: 1,
    details: 2,
    review: 3,
  };

  const currentIndex = stepIndex[step];

  const canGoBack = currentIndex > 0 && !(currentIndex === 3 && isDesigning);

  const handleBack = () => {
    if (!canGoBack) return;
    if (step === 'review') {
      setStep('details');
    } else if (step === 'details') {
      setStep('playbook');
    } else if (step === 'playbook') {
      setStep('goal');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Guided Campaign Launch</h2>
            <p className="text-xs text-gray-500">Perfect for beginners  we will walk you through each step.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-sm"
            disabled={isDesigning}
          >
            Close
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-3 border-b bg-gray-50 text-xs text-gray-600 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {['Choose goal', 'Pick playbook', 'Add details', 'Review'].map((label, idx) => (
              <div key={label} className="flex items-center">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold mr-1 ${
                    idx <= currentIndex ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {idx + 1}
                </div>
                <span className={idx === currentIndex ? 'font-semibold text-gray-900' : ''}>{label}</span>
                {idx < 3 && <span className="mx-2 text-gray-300">/</span>}
              </div>
            ))}
          </div>
          <div className="text-[11px] text-gray-500">
            You can edit everything before saving.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">{renderStep()}</div>

        <div className="px-6 py-3 border-t flex justify-between items-center bg-gray-50">
          <button
            type="button"
            onClick={handleBack}
            disabled={!canGoBack}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Back
          </button>

          <div className="flex items-center gap-3">
            {step !== 'review' && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={isDesigning}
              >
                Cancel
              </button>
            )}
            {step === 'goal' && (
              <button
                type="button"
                onClick={handleNextFromGoal}
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Next: Pick playbook
              </button>
            )}
            {step === 'playbook' && (
              <button
                type="button"
                onClick={handleNextFromPlaybook}
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Next: Add details
              </button>
            )}
            {step === 'details' && (
              <button
                type="button"
                onClick={handleDesign}
                className="px-4 py-2 text-sm rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDesigning}
              >
                {isDesigning ? 'Generating…' : 'Generate sequence'}
              </button>
            )}
            {step === 'review' && (
              <button
                type="button"
                onClick={handleLaunch}
                className="px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700"
                disabled={!designResult}
              >
                Open in editor
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedLaunchModal;
